import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, createSearchParams } from "react-router-dom";
import AppFooter from "@components/AppFooter/AppFooter";
import AppNavbar from "@components/AppNavbar/AppNavbar";
import BlogPostCard from "@components/BlogPostCard/BlogPostCard";
import GeneralConst from "@consts/GeneralConst";
import RoutesConst from "@consts/RoutesConst";
import logoBlack from "@images/logos/logoBlack.png";
import BlogService from "@services/BlogService";
import AlertStrings from "@strings/AlertStrings.json";
import GeneralStrings from "@strings/GeneralStrings.json";
import { getExcerpt, getReadTime } from "@utilities/BlogHelper";
import "./BlogPage.scss";

const title = `Blog | ${GeneralConst.SITE_NAME}`;

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d
    .toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
    .toUpperCase();
};

const formatMonthYear = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

const padNumber = (n: number): string => `N° ${String(n).padStart(2, "0")}`;

const splitTitle = (title: string): { first: string; last: string } => {
  const trimmed = title.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return { first: "", last: trimmed };
  return {
    first: trimmed.slice(0, lastSpace),
    last: trimmed.slice(lastSpace + 1),
  };
};

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    BlogService.getAllBlogPosts()
      .then((response) => {
        setPosts(response?.data ?? []);
      })
      .catch(() => {
        setHasError(true);
      });
  }, []);

  const goToPost = (id: string) =>
    navigate({
      pathname: RoutesConst.BLOG.VIEW_POST,
      search: createSearchParams({ id }).toString(),
    });

  const featuredPost = posts[posts.length - 1] ?? null;
  const archivePosts = posts.slice(1);
  const { first: titleFirst, last: titleLast } = featuredPost
    ? splitTitle(featuredPost.title)
    : { first: "", last: "" };
  const blogStrings = GeneralStrings.blog;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <AppNavbar transparentNavbar={false} />
      <section className="blog-page">
        <div className="container">
          <div className="title-area">
            <h2>{blogStrings.blog}</h2>
            <div className="separator">
              <img src={logoBlack} className="seperator-img" alt="logo" />
            </div>
          </div>
        </div>
        {hasError && posts.length === 0 && (
          <div className="container">
            <p className="blog-load-error">{AlertStrings.generalError}</p>
          </div>
        )}
        {featuredPost && (
          <div className="featured-section">
            <div className="container">
              <div className="featured-layout">
                <div className="featured-image-wrap">
                  <span className="featured-label-top">
                    {blogStrings.featuredEntry} /{" "}
                    {formatMonthYear(featuredPost.date)}
                  </span>
                  <img
                    src={featuredPost.imgLoc}
                    alt={featuredPost.title}
                    className="featured-img"
                    loading="lazy"
                  />
                  <span className="featured-label-bottom">{padNumber(1)}</span>
                </div>

                <div className="featured-content">
                  <p className="featured-eyebrow">{blogStrings.latestEntry}</p>
                  <h2 className="featured-title">
                    {titleFirst && (
                      <span className="title-regular">{titleFirst} </span>
                    )}
                    <em className="title-italic">{titleLast}</em>
                  </h2>
                  <p className="featured-excerpt">
                    {getExcerpt(featuredPost.subtitle)}
                  </p>
                  <hr className="featured-meta-divider" />
                  <p className="featured-meta">
                    <span>{formatDate(featuredPost.date)}</span>
                    <span className="meta-separator">·</span>
                    <span>
                      {getReadTime(featuredPost.content)} {blogStrings.minRead}
                    </span>
                    <span className="meta-separator">·</span>
                    <span>
                      {blogStrings.by} {blogStrings.author}
                    </span>
                  </p>
                  <button
                    className="btn-read-entry"
                    onClick={() => goToPost(featuredPost.id)}
                  >
                    {blogStrings.readTheEntry}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <hr className="blog-divider" />

        {archivePosts.length > 0 && (
          <div className="archive-section">
            <div className="container">
              <div className="archive-header">
                <h2 className="archive-title">
                  <em>{blogStrings.posts}</em>
                </h2>
                {archivePosts.length > 0 && (
                  <span className="archive-count">
                    {String(archivePosts.length).padStart(2, "0")}{" "}
                    {blogStrings.previousPosts}
                  </span>
                )}
              </div>
              <hr className="archive-divider" />
              {archivePosts.length > 0 && (
                <div className="archive-grid">
                  {archivePosts.map((post, idx) => (
                    <BlogPostCard
                      key={post.id}
                      post={post}
                      postNumber={idx + 1}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      <AppFooter />
    </>
  );
};

export default BlogPage;
